<template>
  <!-- ProProxyUI: WireGuard and OpenVPN are always ready on the box, one file each -->
  <div class="modal fade" id="vpnModal" tabindex="-1" aria-hidden="true" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered w-[460px]">
      <div class="modal-content" v-if="props.license">
        <div class="modal-header">
          <h1 class="modal-title fs-5">VPN for {{ props.license.host_domain }}:{{ props.license.http_port }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body px-4">
          <div v-if="loading" class="vpn-dim py-3">Loading…</div>
          <div v-else-if="vpn && vpn.supported" class="space-y-3">
            <div v-if="vpn.wg" class="vpn-box space-y-1">
              <div class="flex items-center justify-between"><span class="flex items-center gap-2"><img :src="wireGuardLogo" alt="" class="vpn-logo vpn-logo--wg"/><b>WireGuard</b></span><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.wg.port }}</span></div>
              <div v-if="vpn.live" class="text-sm" :class="vpn.live.wg_connected ? 'vpn-on' : 'vpn-dim'">
                <v-icon icon="mdi-circle" size="9" class="me-1" :color="vpn.live.wg_connected ? '#22c55e' : '#ef4444'"></v-icon>{{ vpn.live.wg_connected ? 'Connected' : 'Not connected' }}<span v-if="vpn.live.wg_seen" class="vpn-dim"> · last seen {{ ago(vpn.live.wg_seen) }}</span>
              </div>
              <div class="flex gap-3 items-center pt-1">
                <img v-if="wgQrUrl" :src="wgQrUrl" alt="WireGuard QR code" class="vpn-qr"/>
                <div class="space-y-1">
                  <div class="text-sm"><b>On a phone:</b> open the WireGuard app, tap +, then "Scan from QR code".</div>
                  <div class="text-sm vpn-dim">On a computer: download the file. No login needed, the keys are inside it.</div>
                  <div class="flex gap-2 pt-1">
                    <button class="btn btn-primary btn-sm" :disabled="busy" @click="download('wg')">Download .conf file</button>
                    <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="newKeys">New keys</button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="vpn.ovpn" class="vpn-box space-y-1">
              <div class="flex items-center justify-between"><span class="flex items-center gap-2"><img :src="openVpnLogo" alt="" class="vpn-logo"/><b>OpenVPN</b></span><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.ovpn.port }} {{ vpn.ovpn.proto }}</span></div>
              <div v-if="vpn.live" class="text-sm" :class="vpn.live.ovpn_connected ? 'vpn-on' : 'vpn-dim'">
                <v-icon icon="mdi-circle" size="9" class="me-1" :color="vpn.live.ovpn_connected ? '#22c55e' : '#ef4444'"></v-icon>{{ vpn.live.ovpn_connected ? 'Connected' : 'Not connected' }}<span class="vpn-dim"> · {{ vpn.live.ovpn_sessions === 1 ? '1 open session' : (vpn.live.ovpn_sessions || 0) + ' open sessions' }}</span>
              </div>
              <div v-if="!vpn.ovpn_login" class="text-sm vpn-dim">OpenVPN logs in with a username and password. Set a username:password login under Authentication to use it.</div>
              <div v-else class="flex gap-3 items-center pt-1">
                <img v-if="ovpnQrUrl" :src="ovpnQrUrl" alt="OpenVPN QR code" class="vpn-qr"/>
                <div class="space-y-1">
                  <div class="text-sm"><b>On a phone:</b> scan with the camera. The profile downloads: open it with OpenVPN Connect.</div>
                  <div class="text-sm vpn-dim">The QR link works for 10 minutes. Login: your proxy username and password (<span class="font-mono">{{ vpn.user }}</span>).</div>
                  <div class="pt-1"><button class="btn btn-primary btn-sm" :disabled="busy" @click="download('ovpn')">Download .ovpn file</button></div>
                </div>
              </div>
            </div>
            <div class="text-sm vpn-dim">Data today: {{ vpn.used_mb }} MB<span v-if="vpn.quota_gb"> of {{ vpn.quota_gb }} GB</span>, shared by the proxy and the VPN.</div>
            <div class="text-xs vpn-dim">New keys, or a new proxy password, make a new WireGuard file: download it again afterwards.</div>
          </div>
          <div v-else class="vpn-dim py-3">VPN is not available for this proxy.</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, defineProps } from 'vue';
import { useProxyStore, useAlertStore } from "@/stores"
import { wgQr, ovpnQr } from '@/helpers/vpnQr';
import openVpnLogo from '@/assets/openVPN.png';
import wireGuardLogo from '@/assets/wireguard.svg';

const props = defineProps({ license: { type: Object, required: true } });
const proxyStore = useProxyStore();
const alertStore = useAlertStore();

const vpn = ref(null);
const loading = ref(false);
const busy = ref(false);
// QR codes: WireGuard = the profile itself; OpenVPN = a 10-minute download link (made again before it runs out)
const wgQrUrl = ref(null);
const ovpnQrUrl = ref(null);
let ovpnQrAt = 0;
const makeWgQr = async () => { wgQrUrl.value = null; if (vpn.value?.wg) wgQrUrl.value = await wgQr(props.license.uuid); };
const makeOvpnQr = async () => { ovpnQrUrl.value = null; ovpnQrAt = 0; if (vpn.value?.ovpn && vpn.value.ovpn_login) { ovpnQrUrl.value = await ovpnQr(props.license.uuid); ovpnQrAt = Date.now(); } };

async function load() {
  if (!props.license || props.license.m_type !== 'PROPROXYUI') { vpn.value = null; return; }
  loading.value = true;
  vpn.value = await proxyStore.getVpn(props.license.uuid);
  loading.value = false;
  wgQrUrl.value = null; ovpnQrUrl.value = null; ovpnQrAt = 0;
  if (vpn.value?.supported) { makeWgQr(); makeOvpnQr(); }
}
// Load only while this dialog is open (the licence prop is shared with every dialog on the page).
const modalEl = ref(null);
const isOpen = () => !!modalEl.value && modalEl.value.classList.contains('show');
watch(() => props.license, () => { if (isOpen()) load(); });
// the OpenVPN QR link expires after 10 minutes: a fresh one while the window stays open
let qrTimer = null;
const refreshQr = () => { if (isOpen() && ovpnQrAt && Date.now() - ovpnQrAt > 9 * 60 * 1000) makeOvpnQr(); };
// the connected state is live: re-read every 30 s while the window is open (quietly: no error pop-ups)
let liveTimer = null;
const refreshLive = async () => {
  if (!isOpen() || busy.value || !vpn.value?.supported || props.license?.m_type !== 'PROPROXYUI') return;
  const next = await proxyStore.getVpnQuiet(props.license.uuid);
  if (next && vpn.value) vpn.value = { ...vpn.value, live: next.live || null };
};
// "18 s ago", "4 min ago", "2 h ago"
const ago = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return '';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};
onMounted(() => { modalEl.value && modalEl.value.addEventListener('shown.bs.modal', load); qrTimer = setInterval(refreshQr, 30000); liveTimer = setInterval(refreshLive, 30000); });
onBeforeUnmount(() => { modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', load); clearInterval(qrTimer); clearInterval(liveTimer); });

async function newKeys() {
  if (!window.confirm("Make new WireGuard keys? Your downloaded .conf file will stop working.")) return;
  busy.value = true;
  const res = await proxyStore.enableVpn(props.license.uuid, 'wg-new-keys');
  if (res) { vpn.value = res; alertStore.success("New keys made. Scan the new QR code or download the new .conf file."); makeWgQr(); }
  busy.value = false;
}
async function download(type) {
  busy.value = true;
  const f = await proxyStore.vpnFile(props.license.uuid, type);
  if (f && f.content) {
    const blob = new Blob([f.content], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = f.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
  busy.value = false;
}
</script>

<style scoped>
.vpn-box { border: 1px solid #dee2e6; border-radius: 8px; padding: 8px 10px; }
.vpn-dim { color: #6b7280; }
.vpn-on { color: #16a34a; }
.vpn-logo { width: 22px; height: 22px; }
.vpn-logo--wg { background: #ffffff; border-radius: 50%; padding: 2px; }
.vpn-qr { width: 140px; height: 140px; flex: none; background: #ffffff; border-radius: 6px; padding: 4px; }
</style>

<style>
body.dark .vpn-box { border-color: #45445e; background: #34334a; }
body.dark .vpn-dim { color: #bcbbc7; }
body.dark .vpn-on { color: #4ade80; }
</style>
